import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { constants, currencyString } from "@qgiv/core-js";
import { useFormikContext } from "formik";
import { useLocation } from "react-router-dom";
import { ThemeType } from "../../../../../redux/constants";
import {
    removeRegistration,
    selectAllRegistration,
    updateAddAdditionalParticipantDisabled,
    updateCurrentRegistration,
    updateCurrentRegistrationCategoryAndCurrentRegistration,
} from "../../../../../redux/slices/registrationSlice";
import { selectThemeSettings } from "../../../../../redux/slices/cmsSettingsSlice";
import { selectAllCategories } from "../../../../../redux/slices/categoriesSlice";
import {
    selectAllP2pSettings,
    selectBaseUrl,
} from "../../../../../redux/slices/p2pSettingsSlice";
import { selectTeams } from "../../../../../redux/slices/teamsSlice";
import { selectSpecificFormSetting } from "../../../../../redux/slices/formSettingsSlice";
import ParticipantSummary from "./ParticipantSummary";
import { selectIsCms } from "../../../../../redux/slices/configSlice";
import {
    setAppShowRegistrationRole,
    setAppShowRemainingForm,
} from "../../../../../redux/slices/appSettingsSlice";

const ConnectedParticipantSummary = ({ registrationIndex }) => {
    const {
        ENUMS: { CategorySetting },
    } = constants;
    const { MULTI_CATEGORY } = CategorySetting;

    const isCms = useSelector(selectIsCms);

    const { values } = useFormikContext();
    const location = useLocation();
    const { pathname } = location;

    const {
        currentRegistration,
        registrations = [],
        addAdditionalParticipantDisabled = false,
    } = useSelector(selectAllRegistration);
    const themeSettings = useSelector((state) =>
        selectThemeSettings(ThemeType.SUMMARY, state),
    );
    const {
        p2pActionSettings: {
            changeParticipantDetailsLinkLabel = "Change",
            removeParticipantLinkLabel = "Remove",
        },
        p2pRegistration: {
            managedAdditionalParticipantLabel = "You're managing their fundraising",
            nonFundraisingParticipantLabel = "Not fundraising",
            teamCaptainLabel = "Captain",
            commitmentSummaryLabel = "%CommitmentAmount% commitment",
        },
    } = themeSettings;

    const categories = useSelector(selectAllCategories);
    const { additionalRegistrants, categorySystemType } =
        useSelector(selectAllP2pSettings);
    const teams = useSelector(selectTeams);
    const baseUrl = useSelector(selectBaseUrl);
    const currency = useSelector((state) =>
        selectSpecificFormSetting(state, "currency"),
    );

    const registration = registrations[registrationIndex]
        ? registrations[registrationIndex]
        : {};

    const registrationExists = Object.keys(registration).length > 0;

    const getRegistrationValues = () => {
        // This allows us to see current registration info updating in
        // real-time.
        if (
            currentRegistration === registrationIndex &&
            pathname === `${baseUrl}/form/registration`
        ) {
            // Use current formik form's values
            return values;
        }
        // Use stored values from redux
        return registration;
    };
    const {
        Category = [],
        Email = "",
        First_Name = "",
        Is_Nfp = false,
        Last_Name = "",
        Managed_By_Primary = false,
        Team = "",
        Team_Captain = false,
        Needs_To_Be_Reviewed = true,
    } = getRegistrationValues();
    const categoryId = Category[0];
    const categoryName =
        Category[0] && Category[0].length > 0
            ? categories.find(({ id }) => `${id}` === Category[0]).title
            : undefined;
    const hasCommitment =
        Category[0] && Category[0].length > 0
            ? categories.find(({ id }) => `${id}` === Category[0])
                  .enableCommitments
            : undefined;
    const commitmentAmount = hasCommitment
        ? categories.find(({ id }) => `${id}` === Category[0]).commitmentAmount
        : undefined;

    // Only display divider if more than one registration and it is not the last one
    const displayDivider =
        additionalRegistrants && registrations.length - 1 !== registrationIndex;
    const displayDetails = registrationExists; // Only display if registration has previously been saved
    const isConfirmed = !Needs_To_Be_Reviewed;
    const isManagedByPrimary = Managed_By_Primary;
    const isMultiCategory = categorySystemType === MULTI_CATEGORY;
    const isNfp = Is_Nfp;
    const isTeamCaptain = Team_Captain;

    const participantWithoutName = additionalRegistrants
        ? `Participant ${registrationIndex + 1}`
        : `Participant`;

    const participantName =
        registrationExists && First_Name.length > 0 && Last_Name.length > 0
            ? `${First_Name} ${Last_Name}`
            : participantWithoutName;

    const participantEmail = Email;
    const teamName =
        Team.length > 0
            ? teams.find(({ id }) => `${id}` === Team).title
            : undefined;
    const removeAriaLabel = `${removeParticipantLinkLabel} ${participantName} from your registration`;

    const participantCommitmentLabel =
        commitmentSummaryLabel &&
        commitmentSummaryLabel.replace(
            "%CommitmentAmount%",
            currencyString(commitmentAmount, currency),
        );

    const participantSummaryProps = {
        addAdditionalParticipantDisabled,
        baseUrl,
        categoryId,
        categoryName,
        changeParticipantDetailsLinkLabel:
            changeParticipantDetailsLinkLabel || "Change",
        currentRegistration,
        displayDivider,
        displayDetails,
        hasCommitment,
        isCms,
        isConfirmed,
        isManagedByPrimary,
        isMultiCategory,
        isNfp,
        isTeamCaptain,
        managedAdditionalParticipantLabel,
        nonFundraisingParticipantLabel,
        participantCommitmentLabel,
        participantName,
        participantEmail,
        registration,
        registrations,
        registrationIndex,
        removeAriaLabel,
        removeParticipantLinkLabel: removeParticipantLinkLabel || "remove",
        setAppShowRegistrationRole,
        setAppShowRemainingForm,
        teamCaptainLabel,
        teamName,
        removeRegistration,
        updateAddAdditionalParticipantDisabled,
        updateCurrentRegistration,
        updateCurrentRegistrationCategoryAndCurrentRegistration,
    };
    return <ParticipantSummary {...participantSummaryProps} />;
};

ConnectedParticipantSummary.propTypes = {
    registrationIndex: PropTypes.number.isRequired,
};

export default ConnectedParticipantSummary;
